import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics, categories } from '@/mocks/comics';
import type { Comic } from '@/types/comic';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'popular' | 'latest' | 'rating'>('popular');
  const [statusFilter, setStatusFilter] = useState<'all' | '连载中' | '已完结'>('all');

  const currentCat = slug === 'all' ? null : categories.find((c) => c.slug === slug);

  let filtered: Comic[] = slug === 'all'
    ? [...comics]
    : comics.filter((c) => c.category === slug);

  if (statusFilter !== 'all') {
    filtered = filtered.filter((c) => c.status === statusFilter);
  }

  switch (sortBy) {
    case 'latest':
      filtered.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      break;
    case 'rating':
      filtered.sort((a, b) => b.rating - a.rating);
      break;
    case 'popular':
    default:
      filtered.sort((a, b) => b.views - a.views);
      break;
  }

  const pageTitle = currentCat ? `${currentCat.name}漫画` : '全部漫画';
  const pageDesc = currentCat
    ? `韩漫大全为您精选${currentCat.name}类韩国漫画，无遮挡下拉式免费在线观看。每日更新最新${currentCat.name}韩漫，加载迅速无干扰。`
    : '韩漫大全漫画网下拉式免费，无遮挡韩漫下拉式在线观看。浏览全部韩国漫画分类，海量资源实时更新，永久免费。';

  return (
    <>
      <Navbar />
      <main className="pt-20 pb-16">
        <div className="px-4 md:px-6 max-w-screen-2xl mx-auto">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCat ? `${currentCat.name}漫画` : '全部漫画' },
            ]}
          />

          {/* Page Header */}
          <div className="mt-4 mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              {currentCat ? (
                <>
                  <i className={`${currentCat.icon} mr-2 text-primary-500 w-7 h-7 inline-flex items-center justify-center`}></i>
                  {currentCat.name}漫画
                </>
              ) : (
                '全部漫画'
              )}
            </h1>
            <p className="mt-2 text-sm text-foreground-600 max-w-2xl">{pageDesc}</p>
          </div>

          {/* Filter Bar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            {/* Category Tabs */}
            <div className="flex items-center gap-2 flex-wrap">
              <Link
                to="/category/all"
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-label transition-colors cursor-pointer ${
                  slug === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-700'
                }`}
              >
                全部
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-label transition-colors cursor-pointer ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-700'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Sort & Status Filters */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex bg-background-100 rounded-full px-1 py-1">
                {(['popular', 'latest', 'rating'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setSortBy(s)}
                    className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-label transition-colors cursor-pointer ${
                      sortBy === s
                        ? 'bg-background-50 text-foreground-900 font-medium'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {s === 'popular' ? '最热' : s === 'latest' ? '最新' : '评分'}
                  </button>
                ))}
              </div>
              <div className="flex bg-background-100 rounded-full px-1 py-1">
                {(['all', '连载中', '已完结'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-label transition-colors cursor-pointer ${
                      statusFilter === s
                        ? 'bg-background-50 text-foreground-900 font-medium'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {s === 'all' ? '全部' : s}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Results Count */}
          <p className="text-xs text-foreground-500 mb-4">
            共 <strong className="text-foreground-700">{filtered.length}</strong> 部漫画
          </p>

          {/* Comic Grid */}
          {filtered.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {filtered.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-4xl text-foreground-300 w-10 h-10 inline-flex items-center justify-center"></i>
              <p className="mt-3 text-foreground-500">暂无符合条件的漫画</p>
            </div>
          )}
        </div>

        {/* SEO */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              name: pageTitle,
              description: pageDesc,
              url: window.location.href,
              isPartOf: {
                '@type': 'WebSite',
                name: '韩漫大全',
                url: window.location.origin,
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}